import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useContext,
} from "react";
import "./Lookup.scss";
import { useLocation } from "react-router-dom";
import HeaderNavigate from "../../components/headerNavigate/HeaderNavigate";
import { useCustomSearchParams } from "../../components/hooks/useCustomSearchParams";
import { SEARCH_PARAMS } from "../../utils/SearchParams";
import PublicationDetailsCard from "../../components/PublicationDetailsCard/PublicationDetailsCard";
import AxiosService from "../../utils/AxiosService";
import Loader from "../../components/Loader/Loader";
import { AppContext } from "../../context/AppContext";
import { KEY_NAME } from "../../utils/KeyName";
import LookupSidePanel from "../../components/SidePanel/LookupSidePanel";
import Pagination from "../../components/pagination/Pagination";
import ReportExportOptions from "../../components/customReport/ReportExportOptions";
import EntityHeader from "../../components/dashboardLayout/EntityHeader/EntityHeader";
import { getErrorText } from "../../components/DataState/DataState";

const DROPDOWN_OPTIONS = [
  { label: "Author Name", value: "AUTHOR" },
  { label: "DOI", value: "DOI" },
  {
    label: "Funder Name",
    value: "FUNDER",
  },
  { label: "ORCID ID", value: "ORCID" },
];

// const DOI_REGEX = /^10\.\d{4,9}\/[-._;()/:A-Z0-9]+$/i;
const DOI_REGEX =
  /^(https?:\/\/(dx\.)?doi\.org\/)?10\.\d{4,9}\/[-._;()/:A-Z0-9]+$/i;
const ORCID_REGEX = /^(https?:\/\/orcid\.org\/)?\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/i;

const Lookup = () => {
  const { get, setMultiple } = useCustomSearchParams();
  const { entityName, metaInfo } = useContext(AppContext);
  const [shortName, setShortName] = useState(get(SEARCH_PARAMS.SHORT_NAME));
  const [query, setQuery] = useState({
    searchType: get(SEARCH_PARAMS.SEARCH_TYPE),
    searchValue: get(SEARCH_PARAMS.SEARCH_VALUE),
    doi: get(SEARCH_PARAMS.DOI),
    expendId: get(SEARCH_PARAMS.EXPAND_ID),
  });

  const [searchType, setSearchType] = useState(
    DROPDOWN_OPTIONS.filter(
      (item) => item.value === get(SEARCH_PARAMS.SEARCH_TYPE),
    )[0] || DROPDOWN_OPTIONS[0],
  );
  const [searchValue, setSearchValue] = useState(
    get(SEARCH_PARAMS.SEARCH_VALUE) || null,
  );
  const [publicationData, setPublicationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFunderHierarchyLoading, setIsFunderHierarchyLoading] =
    useState(false);
  const [noRecordFound, setNoRecordFound] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [resCode, setResCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const [showSuggestionDropdown, setShowSuggestionDropdown] = useState(false);
  const [funderHierarchySuggestions, setFunderHierarchySuggestions] = useState(
    [],
  );
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [funderHierarchy, setFunderHierarchy] = useState({});
  const [selectedHierarchyNode, setSelectedHierarchyNode] = useState(null);
  const [expandedNodeId, setExpandedNodeId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [regexError, setRegexError] = useState(null);

  const searchInputRef = useRef(null);
  const pendingCursorPosRef = useRef(null);

  useLayoutEffect(() => {
    if (pendingCursorPosRef.current !== null && searchInputRef.current) {
      searchInputRef.current.setSelectionRange(
        pendingCursorPosRef.current,
        pendingCursorPosRef.current,
      );
      pendingCursorPosRef.current = null;
    }
  });

  const axiosService = new AxiosService();

  //FOR FUNDER DASHBOARD
  function getPublishersSuggestions(value) {
    axiosService
      .getPublishersSuggestions({ searchValue: value }, shortName)
      .then((res) => {
        setFunderHierarchySuggestions(res.data);
        setSelectedSuggestion(res.data[0]);
        setShowSuggestionDropdown(true);
      })
      .catch((err) => {
        console.error("Error fetching suggestions:", err);
      });
  }

  function handleReset() {
    setFunderHierarchySuggestions([]);
    setSelectedSuggestion(null);
    setShowSuggestionDropdown(false);
    setFunderHierarchy({});
    setPublicationData(null);
    setSearchValue(null);
    setRegexError(null);
    setNoRecordFound(false);
    setQuery((prev) => ({ ...prev, searchValue: "", doi: "", expendId: "" }));

    setMultiple({
      // [SEARCH_PARAMS.SEARCH_TYPE]: searchType.value,
      [SEARCH_PARAMS.SEARCH_VALUE]: "",
      [SEARCH_PARAMS.DOI]: "",
      [SEARCH_PARAMS.EXPAND_ID]: "",
    });
  }

  //FOR PUBLISHER AND INSTITUTION DASHBOARD
  function getFunderSuggestions(value) {
    axiosService
      .getFunderHierarchySuggestions({ searchValue: value }, shortName)
      .then((res) => {
        setFunderHierarchySuggestions(res.data);
        setSelectedSuggestion(res.data[0]);
        setShowSuggestionDropdown(true);
      })
      .catch((err) => {
        console.error("Error fetching suggestions:", err);
      });
  }

  //FOR ALL DASHBOARD TO GET HIERARCHY
  function getFunderHierarchy(
    doi = "",
    expandId = expandedNodeId,
    isExpand = false,
  ) {
    setMultiple({
      [SEARCH_PARAMS.EXPAND_ID]: expandId,
      // [SEARCH_PARAMS.DOI]: doi,
    });
    if (doi) {
      setIsFunderHierarchyLoading(true);
      axiosService
        .getFunderHierarchy({ doi, expandId, isExpand })
        .then((res) => {
          setFunderHierarchy(res.data);
        })
        .catch((err) => {
          console.error("Error fetching funder hierarchy:", err);
        })
        .finally(() => {
          setIsFunderHierarchyLoading(false);
        });
    } else {
      setFunderHierarchy([]);
    }
  }

  function getLoogupFunder(
    page = currentPage,
    inputValue = searchValue,
    doi = "",
  ) {
    if (searchType.value === "DOI") {
      if (!DOI_REGEX.test(searchValue)) {
        return;
      }
    } else if (searchType.value === "ORCID") {
      if (!ORCID_REGEX.test(searchValue)) {
        return;
      }
    }
    setIsLoading(true);
    setNoRecordFound(false);
    setPublicationData(null);
    setErrorMsg(null);
    const queryData = {
      searchType: searchType.value,
      searchValue: inputValue?.trim(),
      doi: searchType.value === "FUNDER" ? doi : null,
      funderId: doi,
      page: page,
      size: 10,
      includeDescendants: true,
    };
    switch (entityName) {
      case KEY_NAME.FUNDER:
        axiosService
          .getLookupPublicationData(queryData, shortName)
          .then((response) => {
            const resData = response.data;
            if (resData.total === 0) {
              setNoRecordFound(true);
            } else {
              setPublicationData(resData);
              setNoRecordFound(false);
              setErrorMsg(null);
            }
          })
          .catch((error) => {
            setErrorMsg(getErrorText(error));
            console.error("Lookup Publication Error:", error);
          })
          .finally(() => {
            setIsLoading(false); // STOP LOADER
          });

        break;
      case KEY_NAME.PUBLISHER:
      case KEY_NAME.INSTITUTION:
        axiosService
          .getLookupFunder(queryData, shortName)
          .then((response) => {
            const resData = response.data;
            if (resData.total === 0) {
              setNoRecordFound(true);
            } else {
              setPublicationData(resData);
              setNoRecordFound(false);
              setErrorMsg(null);
            }
          })
          .catch((error) => {
            setErrorMsg(getErrorText(error));
            console.error("Lookup Error:", error);
          })
          .finally(() => {
            setIsLoading(false); // STOP LOADER
          });
        break;

      default:
        break;
    }
  }

  useEffect(() => {
    if (currentPage && query.searchValue && entityName) {
      getLoogupFunder(currentPage, query.searchValue, query.doi);
    }
  }, [currentPage, entityName]);

  const handleInputChange = (event) => {
    const value = event?.target?.value ?? "";
    const cursorPos = event?.target?.selectionStart ?? value.length;

    const sanitizedValue = value.replace(/^\s+/, "").replace(/\s+$/, " ");

    const leadingStripped = value.length - value.replace(/^\s+/, "").length;
    pendingCursorPosRef.current = Math.min(
      Math.max(0, cursorPos - leadingStripped),
      sanitizedValue.length,
    );

    const trimmedValue = sanitizedValue.trim();

    setSelectedSuggestionIndex(0);
    setSearchValue(sanitizedValue);
    setQuery((prev) => ({ ...prev, searchValue: sanitizedValue }));

    if (searchType.value === "FUNDER") {
      if (trimmedValue) {
        switch (entityName) {
          case KEY_NAME.FUNDER:
            getPublishersSuggestions(trimmedValue);
            break;
          case KEY_NAME.PUBLISHER:
          case KEY_NAME.INSTITUTION:
            getFunderSuggestions(trimmedValue);
            break;
          // case KEY_NAME.INSTITUTION:
          //   break;

          default:
            break;
        }
      } else {
        setShowSuggestionDropdown(false);
        setFunderHierarchySuggestions([]);
        setSelectedSuggestion(null);
      }
    }
  };

  useEffect(() => {
    if (funderHierarchySuggestions.length > 0) {
      if (!searchValue) {
        setFunderHierarchySuggestions([]);
        setSelectedSuggestion(null);
        setShowSuggestionDropdown(false);
      }
    }
  }, [funderHierarchySuggestions]);

  const handleReportBtn = () => {
    setShowModule(true);
  };

  const handleExportReport = (queryData) => {};

  const handleCloseReport = () => {
    // setResCode("");
    setShowModule(false);
  };

  function getLookupAndHierarchy(doi, name, expandId) {
    // setMultiple({
    //   [SEARCH_PARAMS.SEARCH_TYPE]: searchType.value,
    //   [SEARCH_PARAMS.SEARCH_VALUE]: searchValue,
    // });

    setCurrentPage(1);

    getLoogupFunder(1, name, doi);

    if (searchType.value === "FUNDER") {
      getFunderHierarchy(doi, expandId, false);
    } else {
      setFunderHierarchy({});
    }
  }

  const handleSearch = (event) => {
    if (event) event.preventDefault();
    setShowSuggestionDropdown(false);
    getLookupAndHierarchy(
      selectedSuggestion?.doi, //|| query.doi,
      selectedSuggestion?.name,
    );
    if (searchType.value === "FUNDER") {
      // setMultiple({
      //   [SEARCH_PARAMS.SEARCH_VALUE]: selectedSuggestion?.name || searchValue,
      //   [SEARCH_PARAMS.DOI]: selectedSuggestion?.doi,
      // });
      setQuery((prev) => ({
        ...prev,
        searchValue: selectedSuggestion?.name, //|| query.searchValue,
        doi: selectedSuggestion?.doi, //|| query.doi,
      }));
      setSearchValue(selectedSuggestion?.name);
    } else if (searchType.value === "DOI") {
      if (!DOI_REGEX.test(searchValue)) {
        setRegexError("Please enter a valid DOI.");
        setNoRecordFound(false);
        setPublicationData(null);
        return;
      } else {
        setRegexError(null);
      }
    } else if (searchType.value === "ORCID") {
      if (!ORCID_REGEX.test(searchValue)) {
        setRegexError("Please enter a valid ORCID ID.");
        setNoRecordFound(false);
        setPublicationData(null);
        return;
      } else {
        setRegexError(null);
      }
    }
  };

  useEffect(() => {
    if (selectedHierarchyNode?.name) {
      setFunderHierarchySuggestions([]);
      setFunderHierarchy({});
      setSelectedSuggestion(null);
      setSearchValue(selectedHierarchyNode.name);
      setQuery((prev) => ({
        ...prev,
        searchValue: selectedHierarchyNode.name,
        doi: selectedHierarchyNode.doi,
        expendId: selectedHierarchyNode.id,
      }));
      getLookupAndHierarchy(
        selectedHierarchyNode.doi,
        selectedHierarchyNode.name,
        selectedHierarchyNode.id,
      );
    }
  }, [selectedHierarchyNode]);

  useEffect(() => {
    if (searchType) {
      setMultiple({
        [SEARCH_PARAMS.SEARCH_TYPE]: searchType.value,
        [SEARCH_PARAMS.SEARCH_VALUE]: regexError ? undefined : searchValue,
        [SEARCH_PARAMS.DOI]: query.doi || selectedSuggestion?.doi, //|| get(SEARCH_PARAMS.DOI),
        [SEARCH_PARAMS.EXPAND_ID]: query.expendId,
      });
    }
  }, [searchType, publicationData, noRecordFound, regexError]);

  useEffect(() => {
    if (!searchValue) return;
    // handleSearch();
    getLookupAndHierarchy(query.doi, query.searchValue);
  }, []);

  useEffect(() => {
    if (!searchValue) {
      setNoRecordFound(false);
    }

    if (searchType && searchType.value !== get(SEARCH_PARAMS.SEARCH_TYPE)) {
      setPublicationData(null);
      setSearchValue("");
      setQuery({});
      setFunderHierarchy({});
      setFunderHierarchySuggestions([]);
      setSelectedSuggestion(null);
      setNoRecordFound(false);
      // return;
    }
  }, [searchType]);

  useEffect(() => {
    if (expandedNodeId) {
      getFunderHierarchy(get(SEARCH_PARAMS.DOI), expandedNodeId, true);
    }
  }, [expandedNodeId]);

  return (
    <div className="lookup-page search-box">
      <HeaderNavigate
        label={`Funder Dashboard / ${get(SEARCH_PARAMS.ENTITY_NAME)}`}
      />

      {/* PAGE TITLE */}
      <div className="bg-light text-start py-4 px-3 mb-4">
        <EntityHeader
          metaInfo={metaInfo}
          dashboardType={entityName}
          title={get(SEARCH_PARAMS.ENTITY_NAME)}
        />
      </div>
      <div className="text-start">
        <h4 className="">Lookup</h4>

        <p className="mt-3 mb-1">
          {/* {`To find a specific publication/article, select the type of search
          (DOI${entityName === KEY_NAME.FUNDER ? "/Funder name" : ""}/ORCID ID) and enter the respective data in the search
          field.`} */}
          {`To find a specific publication/article, select the type of search (Author Name/DOI/Funder Name/ORCID ID) and enter the respective data in the search field.`}
        </p>

        <form
          className=""
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <div className="row  ">
            <div className="col-md-9 col-lg-10 mb-3">
              <div className="input-group">
                <select
                  className="form-select "
                  value={searchType.value}
                  onChange={(e) => {
                    const selected = DROPDOWN_OPTIONS.find(
                      (opt) => opt.value === e.target.value,
                    );
                    setSearchType(selected);
                    setRegexError(null);
                  }}
                >
                  {DROPDOWN_OPTIONS.map(
                    (item, index) => (
                      // item.value === "FUNDER" && entityName !== "funder" ? (
                      //   ""
                      // ) : (
                      <option key={index} value={item.value}>
                        {item.label}
                      </option>
                    ),
                    // ),
                  )}
                </select>

                <div className="position-relative" style={{ width: "80%" }}>
                  <div
                    className="autocomplete-suggestion text-truncate"
                    style={{ width: "90%" }}
                  >
                    {searchValue}
                    <span className="ghost-text text-truncate">
                      {selectedSuggestion &&
                      searchValue &&
                      selectedSuggestion.name
                        .toLowerCase()
                        .startsWith(searchValue.toLowerCase())
                        ? selectedSuggestion.name.slice(searchValue.length)
                        : ""}
                    </span>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="search"
                    className="form-control form-select-auto-width rounded-0 rounded-end autocomplete-input "
                    // style={{ width: "64%" }}
                    aria-label="Text input with segmented dropdown button"
                    placeholder={`Enter the ${searchType.label}`}
                    value={searchValue || ""}
                    onFocus={() => {
                      funderHierarchySuggestions.length > 0 &&
                        setShowSuggestionDropdown(true);
                      // searchValue && setShowSuggestionDropdown(true)
                    }}
                    onBlur={() => setShowSuggestionDropdown(false)}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        const nextIndex =
                          selectedSuggestionIndex <
                          funderHierarchySuggestions.length - 1
                            ? selectedSuggestionIndex + 1
                            : 0;
                        setSelectedSuggestionIndex(nextIndex);
                        setSelectedSuggestion(
                          funderHierarchySuggestions[nextIndex],
                        );
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        const prevIndex =
                          selectedSuggestionIndex > 0
                            ? selectedSuggestionIndex - 1
                            : funderHierarchySuggestions.length - 1;
                        setSelectedSuggestionIndex(prevIndex);
                        setSelectedSuggestion(
                          funderHierarchySuggestions[prevIndex],
                        );
                      } else if (e.key === "Enter") {
                        // e.preventDefault();
                      }
                    }}
                  />

                  {searchValue && (
                    <p
                      className={`reset-btn text-danger position-absolute end-0 mt-1 cursor-pointer ${isLoading ? "disabled-reset-btn" : ""}`}
                      onClick={handleReset}
                    >
                      Reset
                    </p>
                  )}

                  {showSuggestionDropdown &&
                    // filtered.length > 0 &&
                    searchType.value === "FUNDER" && (
                      <ul className="list-group position-absolute w-100 shadow-sm z-3 ">
                        {funderHierarchySuggestions?.map((item, index) => (
                          <li
                            key={index}
                            className={`list-group-item list-group-item-action cursor-pointer ${index === selectedSuggestionIndex && "bg-theme text-light"}`}
                            onMouseDown={() => {
                              getLookupAndHierarchy(item.doi, item.name);
                              setSelectedSuggestion(item);
                              setSearchValue(item.name);
                              setShowSuggestionDropdown(false);
                            }}
                          >
                            {item.name}
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              </div>
            </div>
            <div className="col-md-3 col-lg-2 ">
              <button
                type="submit"
                className="btn search-button w-100 mb-3"
                // onClick={handleSearch}
                disabled={!searchValue}
              >
                Search
              </button>
            </div>
          </div>
        </form>
      </div>
      {isLoading ? (
        <div className="d-flex flex-column align-items-center m-5">
          <Loader
            text={
              searchType?.value === "FUNDER"
                ? "Loading data for the selected funder and its descendents funders. This may take a few moments."
                : null
            }
          />
        </div>
      ) : (
        <>
          {
            // searchType.value === "FUNDER" &&
            <>
              {isFunderHierarchyLoading ? (
                <p>Loading Funder Hierarchy...</p>
              ) : (
                funderHierarchy?.role?.length > 0 && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <LookupSidePanel
                        hierarchyData={funderHierarchy?.tree}
                        setSelectedHierarchyNode={setSelectedHierarchyNode}
                        // setExpandedNodeId={setExpandedNodeId}
                      />
                    </div>
                    <hr />
                  </>
                )
              )}
            </>
          }

          {publicationData?.data && (
            <>
              <div className="d-sm-flex align-items-center justify-content-between mb-1 mt-4">
                <p className="fw-semibold">
                  Showing {publicationData.total.toLocaleString("en-US")}{" "}
                  results
                </p>
                <div className="d-flex justify-content-end">
                  <Pagination
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                    totalCount={publicationData.total}
                    currentPage={publicationData.page}
                    pageSize={publicationData.size}
                  />
                </div>
              </div>
              {publicationData?.data.map((item, index) => (
                <PublicationDetailsCard key={index} cardData={item} />
              ))}
            </>
          )}
        </>
      )}

      {showModule && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div className="modal fade show d-block">
            <div className="modal-dialog  mt-5">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-dark">Email Report</h5>
                  <button
                    className="btn-close"
                    onClick={handleCloseReport}
                  ></button>
                </div>

                <div className="modal-body d-flex flex-column">
                  <p>
                    Reports will be sent to the email address entered below.{" "}
                    <br />
                    Extra large reports may be delivered in multiple files by
                    year.
                  </p>

                  <ReportExportOptions
                    handleExportReport={handleExportReport}
                    showTimeFilter={false}
                    handleClose={handleCloseReport}
                    showFileType={false}
                    width="full"
                  />
                  <button
                    type="button"
                    className="btn btn-light mt-2"
                    onClick={handleCloseReport}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {noRecordFound && (
        <div className="text-danger text-start mt-1">
          No record found for this {get(SEARCH_PARAMS.SEARCH_TYPE)}
        </div>
      )}
      {errorMsg && (
        <div className="text-danger text-start mt-1">
          <span>{errorMsg}</span>
        </div>
      )}
      {regexError && (
        <div className="text-danger text-start mt-1">
          <span>{regexError}</span>
        </div>
      )}
    </div>
  );
};

export default Lookup;
